import { SubmissionError } from 'redux-form'
import { catchTimeoutError, catchApiErrors, addWebpackBackendNotFoundError } from '../../../errorhandler/actions/errorActions'
import { getCookieValue } from 'app/actions/genericActionCreator'
import { convertLgyErrorResponseToFormErrorObject } from 'app/utils/formUtils'
import { catchCommonErrors } from 'app/actions/commonActionUtils'
import { resetFfpsUserDetailAction } from './ffpsUserDetailsActions'

const hasContentType = (contentType, response) => response && response.headers && response.headers.get('content-type') && response.headers.get('content-type').includes(contentType)

export const isJson = (response) => hasContentType('application/json', response)

export const isHtml = (response) => hasContentType('text/html', response)

export const isVaafiLoginPage = (response, text) => isHtml(response) && text.includes('VAAFI Login')

export const isWebpackDevCannotConnectToApiBackend = (response, text) => response.status === 504 && text.includes('Error occured while trying to proxy to')

export const fetchUser = (userId) => {
  let secId;
  return (dispatch, getState) => {
    dispatch(fetchingUser(userId))
    /* If the user had found this user from the search page (most likely scenario) then 
        we already have the user object our redux store, so let's fetch it from there if we can */
    const resultsState = getState().adminState.userAdmin.search.results
    const userFromResults = resultsState.filter(r => r.id === userId)[0]

    if (userFromResults) {
      dispatch(fetchedUser(userFromResults))
    }
    // Maybe just fetch anyway, in case result came from an old search. Best of both worlds.
    return fetch(`/lgyhub/api/users/${userId}`, {
      credentials: 'include',
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }),
    }).then((response) => {
      return catchCommonErrors(dispatch, response, `fetching ${userId}`)
    }).then((responseJson) => {
      secId = responseJson.vaEauthSecId
      dispatch(fetchedUser(responseJson))
    }).then(() => {
      return fetch(`/lgyhub/api/users/ffps/${secId}`, {
        credentials: 'include',
        method: 'GET',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }),
      }).then((response) => {
        return catchCommonErrors(dispatch, response, `fetching ffps user for ${secId}`)
      }).then((data) => {
        if(data.ffpsUsername !== null ){
          dispatch(fetchFfpsUserInfoUsername( data.ffpsUsername, data.ffpsAffiliated))
        } else {
          dispatch(resetFfpsUserDetailAction())
        }
      })
    }).catch((error) => {
      console.error('Unexpected error fetching user', error)
    })
  }
}

export const fetchUserEvents = (userId) => {
  return (dispatch) => {
    dispatch(fetchingUserEvents(userId))
    return fetch(`/lgyhub/api/users/${userId}/events`, {
      credentials: 'include',
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }),
    }).then((response) => {
      return catchCommonErrors(dispatch, response, `fetching user events for ${userId}`)
    }).then((responseJson) => {
      dispatch(fetchedUserEvents(userId, responseJson))
    }).catch((error) => {
      console.error('Unexpected error fetching user events: ', error)
    })
  }
}

export const updateUser = (formValues) => {
  // This creates userChanges with all of the properties of formValues save the ID. We don't want to send the ID as a change since it should never change.
  const { id, ...userChanges } = formValues
  return (dispatch) => {
    dispatch(updatingUser(id))
    return fetch(`/lgyhub/api/users/${id}`, {
      credentials: 'include',
      method: 'PATCH',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }),
      body: JSON.stringify(userChanges)
    }).then((response) => {
      return catchCommonErrors(dispatch, response, `updating user ${id}`)
    }).then((responseJson) => {
      dispatch(updatedUser(responseJson))
      if(userChanges.isFfpsAffiliated && typeof userChanges.isFfpsAffiliated !=='undefined') {
        dispatch(resetFfpsUserDetailAction())
      }
      if(!userChanges.isFfpsAffiliated && typeof userChanges.isFfpsAffiliated !=='undefined') {
        dispatch(fetchFfpsUserInfoUsername(userChanges.ffpsUsername, !userChanges.isFfpsAffiliated))
      }
    }).catch((error) => {
      console.error('Unexpected error updating user: ', error)
    }).finally(() => {
      // if we are updating the user, then we know there will be some event updates so we might as well start fetching them now
      dispatch(fetchUserEvents(id))
    })
  }
}

export const updatingUser = (userId) => ({
  type: 'UPDATING_USER',
  userId
})

export const updatedUser = (user) => ({
  type: 'UPDATED_USER',
  user
})

export const fetchingUserEvents = (userId) => ({
  type: 'FETCHING_USER_EVENTS'
})

export const fetchedUserEvents = (userId, events) => ({
  type: 'FETCHED_USER_EVENTS',
  userId,
  events
})

export const fetchingUser = (userId) => ({
  type: 'FETCHING_USER',
  userId
})

export const fetchedUser = (user) => ({
  type: 'FETCHED_USER',
  user
})

export const updatingUserAffiliations = (userId) => ({
  type: 'UPDATING_USER_AFFILIATIONS',
  userId
})

export const fetchFfpsUserInfoUsername = ( ffpsUsername, ffpsAffiliated) => ({
  type: 'FETCHING_FFPS_USER_DETAILS_WITH_USERNAME',
  ffpsUsername,
  ffpsAffiliated
})

export const updateUserAffiliations = (formValues) => {
  // This creates userChanges with all of the properties of formValues save the ID. We don't want to send the ID as a change since it should never change.
  let { id, ...affiliations } = formValues
  return (dispatch, getState) => {
    dispatch(updatingUserAffiliations(id))
    return fetch(`/lgyhub/api/users/${id}/affiliations`, {
      credentials: 'include',
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }),
      body: JSON.stringify(affiliations)
    }).then((response) => {
      return catchCommonErrors(dispatch, response, `updating user (${id}) affiliations`)
    }).then((responseJson) => {
      if (responseJson.status >= 400) {
        // Throw a form compatible exception, mapping the errors to the form fields
        throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
      } else {
        dispatch(fetchedUser(responseJson))
      }
    }).catch((error) => {
      console.error('Unexpected error updating user affiliations: ', error)
      throw error
    }).finally(() => {
      // if we are updating the user, then we know there will be some event updates so we might as well start fetching them now
      dispatch(fetchUserEvents(id))
    })
  }
}

export const createServiceAccount = ({ username, email , accountType}) => {
  console.log('Creating service account for ' + username)
  return (dispatch, getState) => {
    console.log('dispatch', dispatch)
    return fetch(`/lgyhub/api/users/`, {
      credentials: 'include',
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }),
      body: JSON.stringify({ username, email, type: accountType })
    }).then((response) => {
      if (response.ok && isJson(response)) {
        return response.json().then(responseJson => {
          //dispatch(updatedUser(responseJson))
        })
      } else if (response.ok && !isJson(response)) {
        return response.text().then((text) => {
          if (isVaafiLoginPage(response, text)) {
            dispatch(catchTimeoutError())
          }
        })
      } else if (!response.ok && !isJson(response)) {
        return response.text().then((text) => {
          if (isWebpackDevCannotConnectToApiBackend(response, text)) {
            dispatch(addWebpackBackendNotFoundError())
          }
        })
      } else {//if (!response.ok && isJson(response)) 
        return response.json().then(responseJson => {
          if (response.status === 400 && responseJson.errors) {
            // Throw a form compatible exception, mapping the errors to the form fields
            throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
          }
          dispatch(catchApiErrors(response, 'user administration'))
        })
      }
    }).catch((error) => {
      console.error('Error creating user ', error)
      throw error
    })
  }
}